/**
 * Quote Model Provider
 *
 * The single source of truth for a quote request's damage + quote, shared by the
 * damage assessment view (DamagePartsList) and the quote editor (QuoteForm).
 *
 * Two halves of one model:
 *   - `parts`  — the nested damage model (grouped by zone), persisted to
 *                `vehicle_damage.damageItems`. Edited by the damage view; also the
 *                landing zone for AI assessment (picked up via realtime).
 *   - `extras` — quote-only lines (allowances, imported materials/outwork, manual
 *                non-damage lines) that are not derived from a damage part.
 *
 * The quote the user sees is `partsToLines(parts) ++ extras`. This provider is the
 * ONLY writer to both `vehicle_damage` and `quotes`, so there is no dual-writer
 * drift: the damage view mutates `parts`, the quote view mutates `extras` or maps
 * its edits back onto `parts`, and both are projected into `quotes.lines`.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import appwrite, {
  databases,
  COLLECTIONS,
  ID,
  Query,
  DATABASE_ID,
  Permission,
  Role,
} from '../services/appwrite';
import { toast } from 'sonner';
import { dataApi } from '../services/api';
import { useAuth } from './AuthContext';
import {
  VEHICLE_TYPE_SUFFIX,
  PAINT_SIZE_SUFFIX,
  partsToLines,
  computeTotals,
  splitStoredLines,
  computeAllowanceLines,
  allowanceOverrideLine,
  inversePatchForLineEdit,
  operationForRole,
  parsePartMarkups,
  linePartIds,
  isChildPart,
} from '../lib/damageLines';

const QuoteModelContext = createContext(null);

export function useQuoteModel() {
  const ctx = useContext(QuoteModelContext);
  if (!ctx) throw new Error('useQuoteModel must be used within a QuoteModelProvider');
  return ctx;
}

// Group a flat damage-items array (as stored in vehicle_damage) back into the
// zone-keyed shape the damage view works with. Child parts are normalised into
// their parent's zone (mirrors groupDamageItemsByZone in the old DamagePartsList).
function groupByZone(damageItems) {
  const parentZoneMap = {};
  damageItems.forEach((item) => {
    if (!item.forAccess && !item.isChild && !item.forBlend) {
      parentZoneMap[item.id] = item.zone;
    }
  });
  const byZone = {};
  damageItems.forEach((item) => {
    let zone = item.zone;
    if ((item.forAccess || item.isChild || item.forBlend) && item.parent && parentZoneMap[item.parent]) {
      zone = parentZoneMap[item.parent];
    }
    if (!byZone[zone]) byZone[zone] = [];
    byZone[zone].push(item);
  });
  return byZone;
}

// A child insurer config (State/AMI/NZI under IAG) only carries what it differs
// on — every blank field means "same as the parent". Resolve that once here so
// everything downstream reads one effective config, the same way
// QuoteRequestDetails resolves the hourly rate and price-edit permission.
function mergeWithParentConfig(child, parent) {
  if (!child) return parent || null;
  if (!parent) return child;
  const merged = { ...child };
  Object.keys(parent).forEach((key) => {
    if (key.startsWith('$') || key === 'name' || key === 'parentId') return;
    const own = child[key];
    if (own == null || own === '') merged[key] = parent[key];
  });
  return merged;
}

function flattenParts(partsByZone) {
  const items = [];
  Object.keys(partsByZone).forEach((zone) => {
    (partsByZone[zone] || []).forEach((part) => items.push({ ...part, zone }));
  });
  return items;
}

export function QuoteModelProvider({
  quoteRequestId,
  insurerConfigId = null,
  vehicleType = 'standard',
  vehicleSize = 'medium',
  labourRate = 0,
  children,
}) {
  const { user } = useAuth();
  const organisationId = user?.organisationId;

  // ── Damage (parts) state, persisted to vehicle_damage ──────────────────────
  const [parts, setParts] = useState({});
  const [damageDocId, setDamageDocId] = useState(null);
  const [damageReady, setDamageReady] = useState(false);
  const isRealtimeUpdate = useRef(false);
  const ownWritesRef = useRef(new Set());

  // ── Quote state, persisted to quotes ───────────────────────────────────────
  const [quote, setQuote] = useState(null);
  const [extras, setExtras] = useState([]);
  const [notes, setNotes] = useState('');
  const quoteId = quote?.$id || null;
  const linesWriteRef = useRef(new Set()); // echo suppression for our own quotes.lines writes

  // ── Insurer line-allowance cache (pricing + defaults) ──────────────────────
  const insurerCacheRef = useRef(null);
  const insurerCacheConfigRef = useRef(null);
  const insurerConfigRef = useRef(null);
  const [insurerCacheReady, setInsurerCacheReady] = useState(false);

  const rememberOwnWrite = useCallback((json) => {
    ownWritesRef.current.add(json);
    setTimeout(() => ownWritesRef.current.delete(json), 15000);
  }, []);
  const isOwnEcho = useCallback((json) => {
    if (ownWritesRef.current.has(json)) {
      ownWritesRef.current.delete(json);
      return true;
    }
    return false;
  }, []);

  // ── Insurer parts cache ────────────────────────────────────────────────────
  const loadInsurerPartsCache = useCallback(async () => {
    if (!insurerConfigId) return null;
    if (insurerCacheRef.current && insurerCacheConfigRef.current === insurerConfigId) {
      return insurerCacheRef.current;
    }
    const loadLineAllowances = async (configId) => {
      const cache = new Map();
      let lastId = null;
      let hasMore = true;
      while (hasMore) {
        const queries = [Query.equal('insurerConfigId', configId), Query.limit(100)];
        if (lastId) queries.push(Query.cursorAfter(lastId));
        const response = await databases.listDocuments(DATABASE_ID, COLLECTIONS.INSURER_LINE_ALLOWANCES, queries);
        const docs = response.documents || [];
        docs.forEach((p) => cache.set(p.$id, p));
        if (docs.length < 100) hasMore = false;
        else lastId = docs[docs.length - 1].$id;
      }
      return cache;
    };
    try {
      let config = null;
      try {
        config = await databases.getDocument(DATABASE_ID, COLLECTIONS.INSURER_CONFIGS, insurerConfigId);
      } catch (err) {
        console.warn('[QuoteModel] Could not load insurer config', err.message);
      }
      if (config?.parentId) {
        try {
          const parent = await databases.getDocument(DATABASE_ID, COLLECTIONS.INSURER_CONFIGS, config.parentId);
          config = mergeWithParentConfig(config, parent);
        } catch (err) {
          console.warn('[QuoteModel] Could not load parent insurer config', err.message);
        }
      }
      insurerConfigRef.current = config;
      let cache = await loadLineAllowances(insurerConfigId);
      if (cache.size === 0 && config?.parentId) {
        cache = await loadLineAllowances(config.parentId);
      }
      insurerCacheRef.current = cache;
      insurerCacheConfigRef.current = insurerConfigId;
      return cache;
    } catch (err) {
      console.error('[QuoteModel] Error loading insurer parts cache:', err);
      return null;
    }
  }, [insurerConfigId]);

  // Readiness is reset on every insurer change so the flip back to true is what
  // re-renders once the new config has landed in the ref — pricing that reads the
  // ref (paint defaults, part markups) would otherwise keep the old insurer's
  // numbers when a quote is switched from one insurer to another.
  useEffect(() => {
    let cancelled = false;
    setInsurerCacheReady(false);
    if (!insurerConfigId) {
      insurerConfigRef.current = null;
      insurerCacheRef.current = null;
      insurerCacheConfigRef.current = null;
      return undefined;
    }
    loadInsurerPartsCache().then(() => { if (!cancelled) setInsurerCacheReady(true); });
    return () => { cancelled = true; };
  }, [insurerConfigId, loadInsurerPartsCache]);

  const hasInOutPaint = useCallback((lineAllowanceId) => {
    if (!lineAllowanceId || !insurerCacheRef.current) return false;
    const line = insurerCacheRef.current.get(lineAllowanceId);
    if (!line) return false;
    return (Number(line.paintUnitsInOut) || 0) > 0
      || (Number(line.paintUnitsInOutSmall) || 0) > 0
      || (Number(line.paintUnitsInOutLarge) || 0) > 0;
  }, []);

  const resolvePaintSize = useCallback((part) => {
    const basis = insurerConfigRef.current?.paintSizeBasis || 'panel';
    const raw = basis === 'vehicle' ? vehicleSize : part.paintSize;
    const size = String(raw || 'standard').toLowerCase();
    return PAINT_SIZE_SUFFIX[size] !== undefined ? size : 'standard';
  }, [vehicleSize]);

  const getPaintSizeOptions = useCallback((lineAllowanceId, paintScope = 'outer') => {
    if ((insurerConfigRef.current?.paintSizeBasis || 'panel') !== 'panel') return [];
    if (!lineAllowanceId || !insurerCacheRef.current) return [];
    const line = insurerCacheRef.current.get(lineAllowanceId);
    if (!line) return [];
    const base = (paintScope === 'innerOuter' && hasInOutPaint(lineAllowanceId)) ? 'paintUnitsInOut' : 'paintUnits';
    const has = (suffix) => (Number(line[`${base}${suffix}`]) || 0) > 0;
    if (!has('Small') && !has('Large')) return [];
    return [
      ...(has('Small') ? [{ value: 'small', label: 'Small' }] : []),
      { value: 'standard', label: 'Standard' },
      ...(has('Large') ? [{ value: 'large', label: 'Large' }] : []),
    ];
  }, [hasInOutPaint]);

  const getPartDefaults = useCallback((part) => {
    if (!part.lineAllowanceId || !insurerCacheRef.current) return {};
    const line = insurerCacheRef.current.get(part.lineAllowanceId);
    if (!line) return {};
    const toNum = (val) => {
      if (val == null || val === '') return null;
      const n = Number(val);
      return isNaN(n) ? null : n;
    };
    const chain = VEHICLE_TYPE_SUFFIX[vehicleType] || VEHICLE_TYPE_SUFFIX.standard;
    const readHours = (base) => {
      for (const suffix of chain) {
        const value = toNum(line[`${base}${suffix}`]);
        if (value != null && value > 0) return value;
      }
      return null;
    };
    const paintScope = part.paintScope || 'outer';
    const paintSize = resolvePaintSize(part);
    const sizeSuffix = PAINT_SIZE_SUFFIX[paintSize] || '';
    const readPaint = (base) => toNum(line[`${base}${sizeSuffix}`]) ?? toNum(line[base]);
    let paintUnits = null;
    if (paintScope === 'innerOuter') paintUnits = readPaint('paintUnitsInOut');
    if (paintUnits == null) paintUnits = readPaint('paintUnits');
    let defaultPaintCost = null;
    if (paintUnits != null && paintUnits > 0) {
      const rate = toNum(insurerConfigRef.current?.paintUnitPriceStandard);
      if (rate) defaultPaintCost = Math.round(paintUnits * rate * 100) / 100;
    }
    return {
      defaultRepairHours: readHours('repairHours'),
      defaultRemoveRefitHours: readHours('removeRefitHours'),
      defaultPaintCost,
    };
    // insurerCacheReady is a dep so the projection reprices once the insurer line
    // allowance cache finishes loading (getPartDefaults reads it via a ref, which
    // ESLint can't see — hence the disable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, resolvePaintSize, insurerCacheReady]);

  const blendUnitPrice = insurerConfigRef.current?.blendUnitPrice ?? null;

  // The part types this insurer marks up (IAG: OEM 10%, aftermarket/exchange/used
  // 20%). Empty for insurers that set no markup — those quote parts at a
  // percentage the assessor types in. Recomputed when the config lands in the ref,
  // which is what flipping insurerCacheReady signals.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const partMarkups = useMemo(() => parsePartMarkups(insurerConfigRef.current), [insurerConfigId, insurerCacheReady]);

  // ── Insurer quote allowances (admin fees / waste / levies) ─────────────────
  // These become the "default lines" seeded on a quote when an insurer is set.
  const [allowances, setAllowances] = useState([]);
  useEffect(() => {
    if (!insurerConfigId) { setAllowances([]); return undefined; }
    let cancelled = false;
    (async () => {
      const load = async (configId) => {
        const res = await databases.listDocuments(DATABASE_ID, 'insurer_quote_allowances', [
          Query.equal('insurerConfigId', configId), Query.limit(100),
        ]);
        return res.documents || [];
      };
      try {
        let docs = await load(insurerConfigId);
        if (docs.length === 0 && insurerConfigRef.current?.parentId) {
          docs = await load(insurerConfigRef.current.parentId);
        }
        if (!cancelled) setAllowances(docs);
      } catch (err) {
        if (!cancelled) { console.warn('[QuoteModel] Could not load insurer allowances', err.message); setAllowances([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [insurerConfigId, insurerCacheReady]);

  // ── Default child-part resolution (access / blend / replacement) ───────────
  const addResolvedChildren = useCallback(async (zoneId, parentPartId, lineAllowanceId, kind) => {
    const cache = await loadInsurerPartsCache();
    if (!cache) return;
    const parentDoc = cache.get(lineAllowanceId);
    const fieldByKind = {
      access: 'removeRefitAccessParts',
      blend: 'paintBlendParts',
      child: 'relatedReplacementParts',
    };
    const listField = parentDoc?.[fieldByKind[kind]];
    if (!parentDoc || !listField) return;
    let ids = [];
    try {
      ids = typeof listField === 'string' ? JSON.parse(listField) : listField;
      if (!Array.isArray(ids)) ids = [];
    } catch {
      return;
    }
    if (ids.length === 0) return;
    const now = Date.now();
    const newChildren = ids
      .map((cid) => cache.get(cid))
      .filter(Boolean)
      .map((doc) => {
        if (kind === 'access') {
          return {
            id: `${zoneId}-access-${doc.$id}-${now}`, part: doc.lineName, description: '',
            approach: 'remove-refit', removeRefit: true, paint: false, panelbeating: false,
            forAccess: true, parent: parentPartId, lineAllowanceId: doc.$id, images: [], source: 'auto-access',
          };
        }
        if (kind === 'blend') {
          return {
            id: `${zoneId}-blend-${doc.$id}-${now}`, part: doc.lineName, description: '',
            approach: 'blend', removeRefit: false, paint: true, panelbeating: false,
            forBlend: true, parent: parentPartId, lineAllowanceId: doc.$id, images: [], source: 'auto-blend',
          };
        }
        return {
          id: `${zoneId}-child-${doc.$id}-${now}`, part: doc.lineName, description: '',
          approach: 'replace', removeRefit: true, paint: true, panelbeating: false,
          paintScope: hasInOutPaint(doc.$id) ? 'innerOuter' : 'outer', paintSize: 'standard',
          isChild: true, forAccess: false, parent: parentPartId, lineAllowanceId: doc.$id, images: [], source: 'auto-related',
        };
      });
    if (newChildren.length === 0) return;
    setParts((prev) => ({ ...prev, [zoneId]: [...(prev[zoneId] || []), ...newChildren] }));
  }, [loadInsurerPartsCache, hasInOutPaint]);

  const addDefaultAccessParts = useCallback((z, p, l) => addResolvedChildren(z, p, l, 'access'), [addResolvedChildren]);
  const addDefaultBlendParts = useCallback((z, p, l) => addResolvedChildren(z, p, l, 'blend'), [addResolvedChildren]);
  const addDefaultChildParts = useCallback((z, p, l) => addResolvedChildren(z, p, l, 'child'), [addResolvedChildren]);

  // ── Load damage doc (create if missing) ────────────────────────────────────
  useEffect(() => {
    if (!quoteRequestId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await databases.listDocuments(DATABASE_ID, COLLECTIONS.VEHICLE_DAMAGE, [
          Query.equal('quoteRequestId', quoteRequestId),
        ]);
        if (cancelled) return;
        if (response.documents.length > 0) {
          const doc = response.documents[0];
          setDamageDocId(doc.$id);
          try {
            setParts(groupByZone(JSON.parse(doc.damageItems || '[]')));
          } catch {
            setParts({});
          }
        } else if (organisationId) {
          rememberOwnWrite('[]');
          const newDoc = await databases.createDocument(
            DATABASE_ID, COLLECTIONS.VEHICLE_DAMAGE, ID.unique(),
            { organisationId, quoteRequestId, damageItems: '[]' },
            [Permission.read(Role.team(organisationId)), Permission.update(Role.team(organisationId)), Permission.delete(Role.team(organisationId))],
          );
          if (!cancelled) { setDamageDocId(newDoc.$id); setParts({}); }
        }
      } catch (err) {
        console.error('[QuoteModel] Error loading damage:', err);
      } finally {
        if (!cancelled) setDamageReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [quoteRequestId, organisationId, rememberOwnWrite]);

  // Detect AI creating the damage doc before we have one
  useEffect(() => {
    if (!quoteRequestId || damageDocId) return undefined;
    const unsubscribe = appwrite.subscribeToCollection(COLLECTIONS.VEHICLE_DAMAGE, (response) => {
      if (!response.events.includes(`databases.${DATABASE_ID}.collections.vehicle_damage.documents.*.create`)) return;
      const payload = response.payload;
      if (!payload || payload.quoteRequestId !== quoteRequestId) return;
      setDamageDocId(payload.$id);
      const incoming = payload.damageItems || '[]';
      if (isOwnEcho(incoming)) return;
      try {
        isRealtimeUpdate.current = true;
        setParts(groupByZone(JSON.parse(incoming)));
      } catch (err) {
        console.error('[QuoteModel] Error parsing AI damage items:', err);
      }
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [quoteRequestId, damageDocId, isOwnEcho]);

  // Document-level realtime for damage updates
  useEffect(() => {
    if (!damageDocId) return undefined;
    const unsubscribe = appwrite.subscribeToDocument(COLLECTIONS.VEHICLE_DAMAGE, damageDocId, (response) => {
      if (!response.payload) return;
      const incoming = response.payload.damageItems || '[]';
      if (isOwnEcho(incoming)) return;
      try {
        isRealtimeUpdate.current = true;
        setParts(groupByZone(JSON.parse(incoming)));
      } catch (err) {
        console.error('[QuoteModel] Error parsing realtime damage items:', err);
      }
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [damageDocId, isOwnEcho]);

  // Persist parts -> vehicle_damage (debounced)
  useEffect(() => {
    if (!quoteRequestId || !organisationId || !damageReady || !damageDocId) return undefined;
    if (isRealtimeUpdate.current) { isRealtimeUpdate.current = false; return undefined; }
    const timer = setTimeout(async () => {
      try {
        const json = JSON.stringify(flattenParts(parts));
        rememberOwnWrite(json);
        await databases.updateDocument(DATABASE_ID, COLLECTIONS.VEHICLE_DAMAGE, damageDocId, { damageItems: json });
      } catch (err) {
        console.error('[QuoteModel] Error saving damage:', err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [parts, quoteRequestId, organisationId, damageReady, damageDocId, rememberOwnWrite]);

  // ── Load the single quote (create if missing) ──────────────────────────────
  useEffect(() => {
    if (!quoteRequestId || !organisationId) return;
    let cancelled = false;
    (async () => {
      try {
        const q = await dataApi.getOrCreateQuote(quoteRequestId);
        if (cancelled) return;
        setQuote(q);
        setNotes(q.notes || '');
        setExtras(splitStoredLines(q.lines).extras);
      } catch (err) {
        console.error('[QuoteModel] Error loading quote:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [quoteRequestId, organisationId]);

  // Quote realtime: pick up extras/notes edits from other clients
  useEffect(() => {
    if (!quoteId) return undefined;
    const unsubscribe = appwrite.subscribeToDocument(COLLECTIONS.QUOTES, quoteId, (response) => {
      if (!response.payload) return;
      // Keep the record itself current — `lines` is tracked separately below, but
      // flags like isPanelQuoteImport decide how the quote is priced, so a Panel
      // Quote import landing while the request is open must take effect without
      // a reload.
      setQuote((prev) => (prev ? { ...prev, ...response.payload, lines: prev.lines } : prev));
      const incoming = typeof response.payload.lines === 'string' ? response.payload.lines : JSON.stringify(response.payload.lines || []);
      if (linesWriteRef.current.has(incoming)) { linesWriteRef.current.delete(incoming); return; }
      try {
        const parsed = typeof response.payload.lines === 'string' ? JSON.parse(response.payload.lines) : (response.payload.lines || []);
        setExtras(splitStoredLines(parsed).extras);
      } catch { /* ignore */ }
      if (typeof response.payload.notes === 'string') setNotes(response.payload.notes);
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [quoteId]);

  // ── Projected lines + totals ───────────────────────────────────────────────
  const pricingCtx = useMemo(() => ({
    getDefaults: getPartDefaults,
    labourRate,
    blendUnitPrice,
    blendUnits: 1,
    partMarkups,
  }), [getPartDefaults, labourRate, blendUnitPrice, partMarkups]);

  const damageLines = useMemo(() => partsToLines(parts, pricingCtx), [parts, pricingCtx]);
  // A quote imported from Panel Quote is Panel Quote's quote: it already carries
  // that job's fees as its own lines (enviro fee, vehicle scan, materials), so
  // layering our computed default lines on top would charge them twice. The
  // import is a copy, not a starting point.
  const isPanelQuoteImport = Boolean(quote?.isPanelQuoteImport);
  // Allowances are a percentage of the priced work + manual extras, so compute
  // them last and append; they recompute whenever the quote changes.
  const allowanceLines = useMemo(
    () => (isPanelQuoteImport ? [] : computeAllowanceLines(allowances, [...damageLines, ...extras])),
    [isPanelQuoteImport, allowances, damageLines, extras],
  );
  const lines = useMemo(
    () => [...damageLines, ...extras, ...allowanceLines],
    [damageLines, extras, allowanceLines],
  );
  const totals = useMemo(() => computeTotals(lines), [lines]);

  // Persist projection + extras -> quotes.lines (debounced) whenever they change
  const linesJson = useMemo(() => JSON.stringify(lines), [lines]);
  useEffect(() => {
    if (!quoteId || !damageReady) return undefined;
    const timer = setTimeout(async () => {
      try {
        linesWriteRef.current.add(linesJson);
        setTimeout(() => linesWriteRef.current.delete(linesJson), 15000);
        await dataApi.quoteUpdate(quoteId, {
          lines: JSON.parse(linesJson),
          subtotal: totals.subtotal,
          gst: totals.gst,
          total: totals.total,
        });
      } catch (err) {
        console.error('[QuoteModel] Error saving quote lines:', err);
        // A failed save here is invisible otherwise: the editor keeps showing the
        // projection it holds in memory, so the quote looks right while nothing
        // is being written. That is exactly how a broken write went unnoticed
        // until Panel Quote received an empty quote.
        toast.error(`Quote could not be saved: ${err.message}`, { id: 'quote-save-failed' });
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [linesJson, quoteId, damageReady, totals]);

  const updateNotes = useCallback((value) => {
    setNotes(value);
    if (!quoteId) return;
    if (updateNotes._t) clearTimeout(updateNotes._t);
    updateNotes._t = setTimeout(() => {
      dataApi.quoteUpdate(quoteId, { notes: value }).catch((err) => console.error('[QuoteModel] notes save failed', err));
    }, 800);
  }, [quoteId]);

  // ── Extras (quote-only lines) mutators ─────────────────────────────────────
  const addExtraLine = useCallback((line) => {
    setExtras((prev) => [...prev, { id: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, damageManaged: false, source: 'manual', report: false, hours: null, amount: 0, ...line }]);
  }, []);
  const updateExtraLine = useCallback((id, patch) => {
    setExtras((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);
  const removeExtraLine = useCallback((id) => {
    setExtras((prev) => prev.filter((l) => l.id !== id));
  }, []);

  // Overrule a computed insurer allowance. The materials allowance is 5% of the
  // body labour on every schedule, but it is still an allowance — an assessor who
  // knows the job needed more (or less) can say so, and their number then stops
  // moving with the labour. Deleting the line they leave behind restores the
  // calculation.
  const overrideAllowanceLine = useCallback((line, amount) => {
    if (!line?.allowanceId) return;
    setExtras((prev) => {
      const replacement = allowanceOverrideLine(line, amount);
      const existing = prev.findIndex((l) => l.source === 'allowance-override' && l.allowanceId === line.allowanceId);
      if (existing === -1) return [...prev, replacement];
      return prev.map((l, i) => (i === existing ? { ...l, amount: replacement.amount } : l));
    });
  }, []);

  // Add a blend child under an existing painted part (the quote-editor blend flow:
  // "adding a paint line that is a blend — which painted part is it for?").
  const addBlendToPart = useCallback((parentPartId, partName, lineAllowanceId = null) => {
    if (!partName || !partName.trim()) return;
    setParts((prev) => {
      let zone = null;
      Object.keys(prev).forEach((z) => { if (prev[z].some((p) => p.id === parentPartId)) zone = z; });
      if (!zone) return prev;
      const child = {
        id: `${zone}-blend-${Date.now()}`, part: partName.trim(), description: '',
        approach: 'blend', removeRefit: false, paint: true, panelbeating: false,
        forBlend: true, parent: parentPartId, lineAllowanceId: lineAllowanceId || null, images: [], source: 'manual',
      };
      return { ...prev, [zone]: [...prev[zone], child] };
    });
  }, []);

  // Add a damage part from the quote editor so the line appears in the damage
  // assessment. Operation flags come from the quote category; the default section
  // is the matched insurer line's primaryLocation, else 'other'.
  const addDamagePart = useCallback(({ category, name, zone = null, lineAllowanceId = null }) => {
    if (!name || !name.trim()) return;
    let z = zone;
    if (!z && lineAllowanceId && insurerCacheRef.current) {
      z = insurerCacheRef.current.get(lineAllowanceId)?.primaryLocation || null;
    }
    z = z || 'other';
    const flags = { removeRefit: false, panelbeating: false, paint: false, approach: 'repair' };
    if (category === 'Remove & Refit') flags.removeRefit = true;
    else if (category === 'Panel Beating') flags.panelbeating = true;
    else if (category === 'Painting') flags.paint = true;
    else if (category === 'Parts') flags.approach = 'replace';
    const part = {
      id: `${z}-${Date.now()}`, part: name.trim(), description: '', zone: z,
      ...flags, paintScope: 'outer', paintSize: 'standard', blend: false,
      forAccess: false, isChild: false, forBlend: false, parent: null,
      lineAllowanceId: lineAllowanceId || null, images: [], source: 'manual',
    };
    setParts((prev) => ({ ...prev, [z]: [...(prev[z] || []), part] }));
  }, []);

  // Move a part (and its access/blend/replacement children) to a different section.
  const movePartToZone = useCallback((partId, newZone) => {
    setParts((prev) => {
      let exists = false;
      Object.keys(prev).forEach((z) => { if (prev[z].some((p) => p.id === partId)) exists = true; });
      if (!exists) return prev;
      const moveIds = new Set([partId]);
      Object.keys(prev).forEach((z) => prev[z].forEach((p) => { if (p.parent === partId) moveIds.add(p.id); }));
      const next = {};
      Object.keys(prev).forEach((z) => {
        prev[z].forEach((p) => {
          const targetZ = moveIds.has(p.id) ? newZone : z;
          if (!next[targetZ]) next[targetZ] = [];
          next[targetZ].push(p.zone === targetZ ? p : { ...p, zone: targetZ });
        });
      });
      return next;
    });
  }, []);

  // Toggle whether a paint line is a blend. On a primary paint part this flips the
  // part's blend flag (reprices at the blend rate); unticking a dedicated blend
  // child removes it.
  const toggleLineBlend = useCallback((line, checked) => {
    if (line.role === 'blend') {
      if (!checked) {
        const ids = new Set(linePartIds(line));
        setParts((prev) => {
          const next = {};
          Object.keys(prev).forEach((z) => { next[z] = prev[z].filter((p) => !ids.has(p.id)); });
          return next;
        });
      }
      return;
    }
    if (line.role === 'paint' && line.damageManaged) {
      updatePartField(line.groupId, 'blend', checked);
    } else if (!line.damageManaged) {
      updateExtraLine(line.id, { blend: checked });
    }
    // updatePartField / updateExtraLine referenced below are hoisted via useCallback.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Damage-derived line edits from the quote view (map back onto parts) ─────
  // `partId` may be a list: one quote line can stand for several parts once
  // duplicates have been collapsed (a tow bar named as an access part by two
  // panels), and an edit has to reach all of them or the surviving copy changes
  // back the moment the other one is projected.
  const updatePartFields = useCallback((partId, patch) => {
    const ids = new Set(Array.isArray(partId) ? partId : [partId]);
    setParts((prev) => {
      const next = {};
      Object.keys(prev).forEach((z) => {
        next[z] = prev[z].map((p) => (ids.has(p.id) ? { ...p, ...patch } : p));
      });
      return next;
    });
  }, []);

  const updatePartField = useCallback((partId, field, value) => {
    updatePartFields(partId, { [field]: value });
  }, [updatePartFields]);

  const updateDerivedLine = useCallback((line, field, value) => {
    const patch = inversePatchForLineEdit(line, field, value, labourRate);
    if (!patch) return;
    updatePartFields(linePartIds(line), patch);
  }, [labourRate, updatePartFields]);

  const deleteDerivedLine = useCallback((line) => {
    // A line can stand for several parts (duplicates collapsed by the projection),
    // and deleting it has to take all of them: leaving one behind just re-projects
    // the same line, so the delete would look like it did nothing.
    const ids = new Set(linePartIds(line));
    // Blend/access child: remove the child part entirely.
    if (line.role === 'blend' || line.role === 'access') {
      setParts((prev) => {
        const next = {};
        Object.keys(prev).forEach((z) => { next[z] = prev[z].filter((p) => !ids.has(p.id)); });
        return next;
      });
      return;
    }
    // Operation on a primary part: turn the operation off (which cascades children
    // in DamagePartsList's own handlers; here we also drop matching children).
    // Any child folded into this line goes entirely — it exists only to say the
    // part comes off, which is the line being deleted.
    const op = operationForRole(line.role);
    if (op) {
      setParts((prev) => {
        const next = {};
        Object.keys(prev).forEach((z) => {
          next[z] = prev[z]
            .filter((p) => !(ids.has(p.id) && isChildPart(p)))
            .map((p) => (ids.has(p.id) ? { ...p, [op]: false } : p))
            .filter((p) => {
              if (!ids.has(p.parent)) return true;
              if (op === 'removeRefit') return !p.forAccess;
              if (op === 'paint') return !p.forBlend;
              return true;
            });
        });
        return next;
      });
      return;
    }
    // A Parts line or "Other": clearing it means switching the part off replace /
    // removing it. Safest is to drop the whole primary part.
    setParts((prev) => {
      const next = {};
      Object.keys(prev).forEach((z) => {
        next[z] = prev[z].filter((p) => !ids.has(p.id) && !ids.has(p.parent));
      });
      return next;
    });
  }, []);

  const value = {
    // ids / readiness
    quoteRequestId, quoteId, damageDocId, damageReady, organisationId,
    insurerConfigId, vehicleType, vehicleSize, labourRate,
    // damage state
    parts, setParts,
    // insurer helpers (used by the damage view)
    insurerCacheReady, insurerConfig: insurerConfigRef.current, insurerCacheRef,
    loadInsurerPartsCache, getPartDefaults, hasInOutPaint, getPaintSizeOptions, resolvePaintSize,
    addDefaultAccessParts, addDefaultBlendParts, addDefaultChildParts, blendUnitPrice, partMarkups,
    // quote state
    quote, lines, damageLines, extras, totals, notes, updateNotes, isPanelQuoteImport,
    // quote mutators
    addExtraLine, updateExtraLine, removeExtraLine, overrideAllowanceLine, addBlendToPart,
    addDamagePart, movePartToZone, toggleLineBlend,
    updateDerivedLine, deleteDerivedLine, updatePartField, updatePartFields,
  };

  return <QuoteModelContext.Provider value={value}>{children}</QuoteModelContext.Provider>;
}

export default QuoteModelContext;
